import React, { useState } from 'react';
import { BuildingIcon, BookIcon, ArrowRightIcon, InfoIcon } from 'lucide-react';
interface OrgUnit {
  id: string;
  code: string;
  name: string;
  glCodeSegment?: string;
  participatesInGLCoding: boolean;
  path: string;
}
interface COASegment {
  id: string;
  segmentCode: string;
  segmentName: string;
  fullGLPath: string;
  level: number;
}
interface OrgUnitGLIntegrationProps {
  orgUnits: OrgUnit[];
  coaSegments: COASegment[];
}
export function OrgUnitGLIntegration({
  orgUnits,
  coaSegments
}: OrgUnitGLIntegrationProps) {
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrgUnit | null>(null);
  const [selectedGLCode, setSelectedGLCode] = useState<COASegment | null>(null);
  const fullPath = selectedOrgUnit && selectedGLCode ? `${selectedOrgUnit.glCodeSegment}.${selectedGLCode.fullGLPath}` : selectedOrgUnit?.glCodeSegment || selectedGLCode?.fullGLPath || '';
  // Group GL codes by depth for better visualization
  const glCodesByLevel = coaSegments.reduce((acc, segment) => {
    if (!acc[segment.level]) {
      acc[segment.level] = [];
    }
    acc[segment.level].push(segment);
    return acc;
  }, {} as Record<number, COASegment[]>);
  const maxLevel = Math.max(...Object.keys(glCodesByLevel).map(Number));
  return <div className="bg-white shadow rounded-lg p-6" data-id="element-10653">
      <div className="mb-6" data-id="element-10654">
        <h3 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-10655">
          <InfoIcon className="h-5 w-5 mr-2 text-indigo-600" data-id="element-10656" />
          OrgUnit & GL Code Integration
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-10657">
          Invoice coding combines Organisation Unit segments with Chart of
          Accounts segments to create the complete coding path. GL codes can be
          up to 5 levels deep after the OrgUnit segment.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-10658">
        {/* OrgUnit Selection */}
        <div className="space-y-3" data-id="element-10659">
          <div className="flex items-center text-sm font-medium text-gray-700" data-id="element-10660">
            <BuildingIcon className="h-4 w-4 mr-2 text-orange-600" data-id="element-10661" />
            Organisation Unit
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-md p-2" data-id="element-10662">
            {orgUnits.filter(ou => ou.participatesInGLCoding).map(orgUnit => <button key={orgUnit.id} onClick={() => setSelectedOrgUnit(orgUnit)} className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${selectedOrgUnit?.id === orgUnit.id ? 'bg-orange-100 border-2 border-orange-500 text-orange-900' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100 text-gray-700'}`} data-id="element-10663">
                  <div className="font-medium" data-id="element-10664">{orgUnit.name}</div>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-10665">
                    {orgUnit.path}
                  </div>
                  {orgUnit.glCodeSegment && <div className="text-xs font-mono font-bold mt-1 text-orange-700" data-id="element-10666">
                      Segment: {orgUnit.glCodeSegment}
                    </div>}
                </button>)}
          </div>
        </div>

        {/* Arrow */}
        <div className="flex items-center justify-center" data-id="element-10667">
          <div className="text-center" data-id="element-10668">
            <ArrowRightIcon className="h-8 w-8 text-gray-400 mx-auto" data-id="element-10669" />
            <span className="text-xs text-gray-500 mt-2 block" data-id="element-10670">Combined</span>
          </div>
        </div>

        {/* GL Code Selection - Grouped by Level */}
        <div className="space-y-3" data-id="element-10671">
          <div className="flex items-center justify-between text-sm font-medium text-gray-700" data-id="element-10672">
            <div className="flex items-center" data-id="element-10673">
              <BookIcon className="h-4 w-4 mr-2 text-indigo-600" data-id="element-10674" />
              GL Code
            </div>
            <span className="text-xs text-gray-500" data-id="element-10675">
              Max Depth: Level {maxLevel}
            </span>
          </div>
          <div className="space-y-4 max-h-96 overflow-y-auto border border-gray-200 rounded-md p-2" data-id="element-10676">
            {Object.keys(glCodesByLevel).map(Number).sort((a, b) => a - b).map(level => <div key={level} data-id="element-10677">
                  <div className="text-xs font-medium text-gray-500 mb-2 px-2" data-id="element-10678">
                    Level {level}
                  </div>
                  <div className="space-y-2" data-id="element-10679">
                    {glCodesByLevel[level].map(segment => <button key={segment.id} onClick={() => setSelectedGLCode(segment)} className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${selectedGLCode?.id === segment.id ? 'bg-indigo-100 border-2 border-indigo-500 text-indigo-900' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100 text-gray-700'}`} style={{
                marginLeft: `${(level - 1) * 8}px`
              }} data-id="element-10680">
                        <div className="font-medium truncate" data-id="element-10681">
                          {segment.segmentName}
                        </div>
                        <div className="text-xs font-mono font-bold mt-1 text-indigo-700" data-id="element-10682">
                          {segment.fullGLPath}
                        </div>
                        <div className="text-xs text-gray-500 mt-1" data-id="element-10683">
                          Level {segment.level} •{' '}
                          {segment.fullGLPath.split('.').length} segment(s)
                        </div>
                      </button>)}
                  </div>
                </div>)}
          </div>
        </div>
      </div>

      {/* Result Display */}
      {fullPath && <div className="mt-6 bg-gradient-to-r from-orange-50 to-indigo-50 border-2 border-indigo-300 rounded-lg p-4" data-id="element-10684">
          <div className="text-center" data-id="element-10685">
            <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-10686">
              Complete Invoice Coding Path
            </p>
            <div className="flex items-center justify-center space-x-2 flex-wrap" data-id="element-10687">
              {selectedOrgUnit?.glCodeSegment && <>
                  <span className="inline-flex items-center px-3 py-1 rounded-md bg-orange-100 border border-orange-300 text-sm font-mono font-bold text-orange-900" data-id="element-10688">
                    <BuildingIcon className="h-3 w-3 mr-1" data-id="element-10689" />
                    {selectedOrgUnit.glCodeSegment}
                  </span>
                  <span className="text-gray-400 font-bold" data-id="element-10690">.</span>
                </>}
              {selectedGLCode?.fullGLPath && <span className="inline-flex items-center px-3 py-1 rounded-md bg-indigo-100 border border-indigo-300 text-sm font-mono font-bold text-indigo-900" data-id="element-10691">
                  <BookIcon className="h-3 w-3 mr-1" data-id="element-10692" />
                  {selectedGLCode.fullGLPath}
                </span>}
            </div>
            <p className="text-2xl font-mono font-bold text-gray-900 mt-3" data-id="element-10693">
              {fullPath}
            </p>
            {selectedGLCode && <p className="text-xs text-gray-600 mt-2" data-id="element-10694">
                Total segments: {fullPath.split('.').length} (1 OrgUnit +{' '}
                {selectedGLCode.fullGLPath.split('.').length} GL Code)
              </p>}
          </div>
        </div>}

      {/* Help Text */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4" data-id="element-10695">
        <div className="flex" data-id="element-10696">
          <InfoIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" data-id="element-10697" />
          <div className="ml-3" data-id="element-10698">
            <h4 className="text-sm font-medium text-blue-900" data-id="element-10699">How it works</h4>
            <div className="mt-2 text-sm text-blue-700 space-y-1" data-id="element-10700">
              <p data-id="element-10701">
                • <strong data-id="element-10702">Organisation Units</strong> provide the first segment
                (e.g., department, location)
              </p>
              <p data-id="element-10703">
                • <strong data-id="element-10704">GL Codes</strong> from Chart of Accounts provide the
                account classification
              </p>
              <p data-id="element-10705">
                • <strong data-id="element-10706">Deep Hierarchies</strong>: GL codes can be nested up
                to 5 levels deep for detailed categorization
              </p>
              <p data-id="element-10707">
                • Together they form the <strong data-id="element-10708">complete coding path</strong>{' '}
                for invoice line items
              </p>
              <p className="mt-2 text-xs" data-id="element-10709">
                Example:{' '}
                <span className="font-mono font-bold" data-id="element-10710">
                  115.5000.5100.5110.5111.5111A
                </span>{' '}
                = OrgUnit (115) + 5-level GL Code (5000.5100.5110.5111.5111A)
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}